import React, { useCallback, useEffect, useState } from 'react'
import { Button, H2 } from '../../lib/components'
import { tradingAdminServices } from '../../services'
import { easternToday } from './instant-funding-reconciliation'

type JournalIssue =
  | 'NOT_SUBMITTED'
  | 'MISSING_AT_ALPACA'
  | 'MISSING_LOCALLY'
  | 'AMOUNT_MISMATCH'
  | 'WALLET_MISMATCH'

type JournalItem = {
  journalId: number | null
  alpacaJournalId: string | null
  investorId: number | null
  direction: 'DEPOSIT' | 'WITHDRAWAL' | null
  localStatus: string | null
  alpacaStatus: string | null
  localAmountCents: number | null
  alpacaAmountCents: number | null
  walletEffectCents: number | null
  createdAt: string | null
  issues: JournalIssue[]
}

export type JournalReconciliationData = {
  systemDate: string
  float: {
    accountId: string
    accountNumber: string | null
    cashCents: number
    lowBalanceCents: number
    isLow: boolean
  } | null
  items: JournalItem[]
  totals: {
    depositsCents: number
    withdrawalsCents: number
    netToReplenishCents: number
    walletDebitedCents: number
    walletCreditedCents: number
  }
  issueCount: number
  balanced: boolean
}

const ISSUE_LABELS: Record<JournalIssue, string> = {
  NOT_SUBMITTED: 'Not sent to Alpaca',
  MISSING_AT_ALPACA: 'Missing at Alpaca',
  MISSING_LOCALLY: 'No Greenback record',
  AMOUNT_MISMATCH: 'Amount differs',
  WALLET_MISMATCH: 'Wallet ledger differs',
}

const DIRECTION_LABELS = {
  DEPOSIT: 'Wallet → brokerage',
  WITHDRAWAL: 'Brokerage → wallet',
}

const usd = (cents: number | null) =>
  cents === null
    ? '—'
    : (cents / 100).toLocaleString('en-US', { style: 'currency', currency: 'USD' })

const signedUsd = (cents: number | null) =>
  cents === null ? '—' : cents > 0 ? `+${usd(cents)}` : usd(cents)

/**
 * Pre-funding view: the firm's float at Alpaca, how much to send to keep it
 * topped up, and a match of wallet ledger, Greenback journals and Alpaca
 * journals for one date. Withdrawals back to the wallet appear in every mode.
 */
const JournalReconciliation = ({ showFloat }: { showFloat: boolean }) => {
  const [systemDate, setSystemDate] = useState(easternToday())
  const [data, setData] = useState<JournalReconciliationData | null>(null)
  const [onlyIssues, setOnlyIssues] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async (date: string) => {
    setLoading(true)
    setError('')
    const res = await tradingAdminServices.getJournalReconciliation(date)
    if (res?.status === 200) {
      setData(res.data)
    } else {
      setData(null)
      setError(res?.data?.error?.message || 'Could not load the journal reconciliation.')
    }
    setLoading(false)
  }, [])

  useEffect(() => {
    void load(systemDate)
  }, [load, systemDate])

  const items = data ? data.items.filter((i) => !onlyIssues || i.issues.length > 0) : []

  return (
    <section className='funding-recon'>
      <H2
        text={showFloat ? 'Pre-funding float & journals' : 'Withdrawals to Greenback wallet'}
        classes={['mb-24']}
      />
      <p className='funding-recon__intro'>
        {showFloat
          ? "Investors' brokerage accounts are loaded by journaling cash from the firm's float account at Alpaca. Keep the float topped up with the day's net deposits."
          : 'Cash investors move from their brokerage account back to their Greenback wallet, journaled to the firm account.'}
      </p>
      <div className='funding-recon__toolbar'>
        <label>
          <span>Date</span>
          <input
            type='date'
            aria-label='Journal date'
            value={systemDate}
            onChange={(e) => e.target.value && setSystemDate(e.target.value)}
          />
        </label>
        <label className='funding-recon__check'>
          <input
            type='checkbox'
            checked={onlyIssues}
            onChange={(e) => setOnlyIssues(e.target.checked)}
          />
          <span>Only show issues</span>
        </label>
        <Button
          text={loading ? 'Loading…' : 'Refresh'}
          variant='secondary-btn'
          isDisabled={loading}
          handleClick={() => void load(systemDate)}
        />
      </div>

      {error ? <p className='wallet-admin__empty'>{error}</p> : null}

      {data ? (
        <>
          {showFloat ? (
            data.float ? (
              <div
                className={`funding-recon__float ${
                  data.float.isLow ? 'funding-recon__float--low' : ''
                }`}
              >
                <div>
                  <span>Float account {data.float.accountNumber || ''}</span>
                  <strong>{usd(data.float.cashCents)}</strong>
                </div>
                <p>
                  {data.float.isLow
                    ? `Below the ${usd(
                        data.float.lowBalanceCents,
                      )} minimum. Top it up so investors can keep adding funds.`
                    : `Above the ${usd(data.float.lowBalanceCents)} minimum.`}{' '}
                  {data.totals.netToReplenishCents > 0
                    ? `Send ${usd(
                        data.totals.netToReplenishCents,
                      )} to cover this date's net deposits.`
                    : 'Nothing to replenish for this date.'}
                </p>
              </div>
            ) : (
              <p className='funding-recon__error'>
                The float account balance could not be read. Check ALPACA_FLOAT_ACCOUNT_ID.
              </p>
            )
          ) : null}

          <div
            role='status'
            className={`funding-recon__banner ${
              data.balanced ? 'funding-recon__banner--ok' : 'funding-recon__banner--bad'
            }`}
          >
            {data.balanced
              ? 'Balanced: the wallet, Greenback records and Alpaca agree.'
              : `${data.issueCount} journal${data.issueCount === 1 ? '' : 's'} need attention.`}
          </div>

          <dl className='funding-recon__totals'>
            <div>
              <dt>Deposits (wallet → brokerage)</dt>
              <dd>{usd(data.totals.depositsCents)}</dd>
            </div>
            <div>
              <dt>Withdrawals (brokerage → wallet)</dt>
              <dd>{usd(data.totals.withdrawalsCents)}</dd>
            </div>
            <div>
              <dt>Net to replenish</dt>
              <dd>{usd(data.totals.netToReplenishCents)}</dd>
            </div>
            <div>
              <dt>Wallet debited</dt>
              <dd>{usd(data.totals.walletDebitedCents)}</dd>
            </div>
            <div>
              <dt>Wallet credited</dt>
              <dd>{usd(data.totals.walletCreditedCents)}</dd>
            </div>
          </dl>

          {items.length === 0 ? (
            <p className='wallet-admin__empty'>
              {onlyIssues ? 'No issues for this date.' : 'No journals for this date.'}
            </p>
          ) : (
            <div className='w-100 table_scroll_horizontal'>
              <table className='styled-table'>
                <thead>
                  <tr>
                    <th>Journal</th>
                    <th>Investor</th>
                    <th>Direction</th>
                    <th>Wallet effect</th>
                    <th>Greenback amount</th>
                    <th>Alpaca amount</th>
                    <th>Status (Greenback / Alpaca)</th>
                    <th>Issues</th>
                  </tr>
                </thead>
                <tbody>
                  {items.map((item) => (
                    <tr
                      key={item.journalId ?? item.alpacaJournalId}
                      className={item.issues.length ? 'funding-recon__row--bad' : undefined}
                    >
                      <td>
                        {item.journalId ? `#${item.journalId}` : '—'}
                        {item.alpacaJournalId ? <small>{item.alpacaJournalId}</small> : null}
                      </td>
                      <td>{item.investorId ?? '—'}</td>
                      <td>{item.direction ? DIRECTION_LABELS[item.direction] : '—'}</td>
                      <td>{signedUsd(item.walletEffectCents)}</td>
                      <td>{usd(item.localAmountCents)}</td>
                      <td>{usd(item.alpacaAmountCents)}</td>
                      <td>
                        {item.localStatus || '—'} / {item.alpacaStatus || '—'}
                      </td>
                      <td>
                        {item.issues.length === 0 ? (
                          'OK'
                        ) : (
                          <ul className='funding-recon__issues'>
                            {item.issues.map((issue) => (
                              <li key={issue}>{ISSUE_LABELS[issue] ?? issue}</li>
                            ))}
                          </ul>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </>
      ) : null}
    </section>
  )
}

export { JournalReconciliation }
